import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Card, Typography, Button, Form, Input, InputNumber, Table, Modal, Space } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Product as ProductType } from '@wbstats/shared';
import type { RootState } from '../store';
import { PageLayout } from '../components/PageLayout';
import { useProductOperations, type CreateProductForm } from '../hooks';

const { Title, Text } = Typography;

export const Product: React.FC = () => {
  const { currentUser, isLoading: userLoading } = useSelector((state: RootState) => state.user);
  const [form] = Form.useForm();
  const token = localStorage.getItem('token');

  const {
    products,
    isLoading,
    isCreating,
    isDeleting,
    selectedProducts,
    fetchProducts,
    createProduct,
    deleteProducts,
    setSelectedProducts,
  } = useProductOperations(token || undefined);

  // Fetch products when user has API token
  useEffect(() => {
    if (currentUser?.wb_api_token) {
      void fetchProducts();
    }
  }, [fetchProducts, currentUser?.wb_api_token]);

  // Handle form submission
  const handleSubmit = async (values: CreateProductForm) => {
    const success = await createProduct(values);
    if (success) {
      form.resetFields();
    }
  };

  // Handle bulk delete with confirmation
  const handleDeleteProducts = () => {
    if (selectedProducts.length === 0) return;

    Modal.confirm({
      title: 'Удаление товаров',
      content: `Вы уверены, что хотите удалить ${selectedProducts.length} выбранн${
        selectedProducts.length > 1 ? 'ых товаров' : 'ый товар'
      }?`,
      okText: 'Да',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: () => {
        void deleteProducts(selectedProducts);
      },
    });
  };

  // Table columns configuration
  const columns: ColumnsType<ProductType> = [
    {
      title: 'ID товара',
      dataIndex: 'productId',
      key: 'productId',
      sorter: (a, b) => a.productId - b.productId,
    },
    {
      title: 'Артикул продавца',
      dataIndex: 'vendor_number',
      key: 'vendor_number',
      sorter: (a, b) => a.vendor_number.localeCompare(b.vendor_number),
    },
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Описание',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => text || '-',
    },
    {
      title: 'WB Seller SID',
      dataIndex: 'wb_seller_sid',
      key: 'wb_seller_sid',
    },
  ];

  if (!currentUser) return null;

  return (
    <PageLayout title="Товары">
      {userLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
          Загрузка...
        </div>
      ) : currentUser?.wb_api_token ? (
        <>
          {/* Product Creation Form */}
          <Card
            title={
              <Title level={4} style={{ marginBottom: 0 }}>
                Создать новый товар
              </Title>
            }
            style={{ marginBottom: 16 }}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={(values: CreateProductForm) => void handleSubmit(values)}
            >
              <Form.Item
                label="ID товара"
                name="productId"
                rules={[
                  { required: true, message: 'Пожалуйста, введите ID товара' },
                  { type: 'number', message: 'ID товара должен быть числом' },
                ]}
              >
                <InputNumber
                  placeholder="Введите ID товара"
                  style={{ width: '100%' }}
                  min={1}
                  precision={0}
                />
              </Form.Item>

              <Form.Item
                label="Артикул продавца"
                name="vendor_number"
                rules={[
                  {
                    required: true,
                    message: 'Пожалуйста, введите артикул продавца',
                  },
                ]}
              >
                <Input placeholder="Введите артикул продавца" />
              </Form.Item>

              <Form.Item
                label="Название товара"
                name="name"
                rules={[
                  {
                    required: true,
                    message: 'Пожалуйста, введите название товара',
                  },
                ]}
              >
                <Input placeholder="Введите название товара" />
              </Form.Item>

              <Form.Item label="Описание" name="description">
                <Input.TextArea placeholder="Введите описание товара (необязательно)" rows={3} />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isCreating}
                  style={{ marginRight: 8 }}
                >
                  Создать товар
                </Button>
                <Button onClick={() => form.resetFields()}>Очистить</Button>
              </Form.Item>
            </Form>
          </Card>

          {/* Products Table */}
          <Card
            title={
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <Title level={4} style={{ marginBottom: 0 }}>
                  Список товаров ({products.length})
                </Title>
                {selectedProducts.length > 0 && (
                  <Space>
                    <Text type="secondary">Выбрано: {selectedProducts.length}</Text>
                    <Button
                      type="primary"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={handleDeleteProducts}
                      loading={isDeleting}
                    >
                      Удалить выбранные
                    </Button>
                  </Space>
                )}
              </div>
            }
          >
            <Table
              dataSource={products}
              columns={columns}
              loading={isLoading}
              rowKey="productId"
              style={{ width: '100%' }}
              rowSelection={{
                type: 'checkbox',
                selectedRowKeys: selectedProducts,
                onChange: (selectedRowKeys) => {
                  setSelectedProducts(selectedRowKeys as number[]);
                },
                getCheckboxProps: (record) => ({
                  name: record.name,
                }),
              }}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} товаров`,
              }}
              scroll={{ x: 'max-content' }}
            />
          </Card>
        </>
      ) : (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Title level={3}>Настройка API токена</Title>
            <Text type="secondary">
              Для управления товарами необходимо настроить API токен Wildberries в разделе{' '}
              &ldquo;Настройки&rdquo;.
            </Text>
          </div>
        </Card>
      )}
    </PageLayout>
  );
};
